import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const Lieu = () => {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="lieu" data-testid="lieu-section" className="relative">
      <div ref={ref} className="grain relative h-[80vh] min-h-[560px] overflow-hidden">
        <motion.img
          style={{ y }}
          src="/images/lieux/baie-abbaye-01.jpeg"
          alt={t.lieu.captions[0]}
          className="absolute inset-0 h-[124%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1600px] items-center px-4 sm:px-8 lg:px-16">
          <div className="max-w-2xl">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.lieu.kicker}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-parchment sm:text-5xl lg:text-6xl">
                {t.lieu.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base font-light leading-relaxed text-parchment/85 sm:text-lg">
                {t.lieu.text}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p data-testid="lieu-address" className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-gold/90">
                <MapPin size={14} /> {t.lieu.address}
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-6 px-4 py-20 sm:grid-cols-3 sm:px-8 lg:px-16 lg:py-28">
        {[
          { src: "/images/lieux/prieure-02.jpeg", caption: t.lieu.captions[1], offset: "sm:mt-0" },
          { src: "/images/lieux/prieure-08.webp", caption: lang === "fr" ? "La tour et le four banal" : "The tower and the banal oven", offset: "sm:mt-14" },
          { src: "/images/lieux/prieure-09.webp", caption: t.lieu.captions[2], offset: "sm:mt-28" },
          { src: "/images/lieux/prieure-10.webp", caption: lang === "fr" ? "Le logis et le puits" : "The lodging house and the well", offset: "sm:mt-0" },
          { src: "/images/lieux/lieu-04.jpeg", caption: t.lieu.captions[3], offset: "sm:mt-14" },
        ].map((img, i) => (
          <Reveal key={img.src} delay={i * 0.12} className={img.offset}>
            <figure className="group">
              <div className="gold-frame relative aspect-[3/4] overflow-hidden">
                <img
                  src={img.src}
                  alt={img.caption}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {img.caption}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default Lieu;
